/**
 * Knowledge section (§6.5): file dropzone + per-file status chips with 3s
 * polling while anything is pending/processing, knowledge_mode radio
 * (auto / inject / retrieve in plain language), and retrieval_top_k slider.
 *
 * Uploads never block the form (§6.5: creation must not block on ingestion).
 * On /agents/new there is no agent to attach files to yet — the dropzone is
 * disabled with a clear hint until the first save.
 */

import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";

import { Button } from "../Button";
import { Slider } from "../Slider";
import { StatusDot, documentStatusTone } from "../StatusDot";
import { api } from "../../lib/api";
import { cx, formatBytes } from "../../lib/format";
import type { AgentConfig, DocumentInfo, KnowledgeMode } from "../../lib/types";
import { KNOWLEDGE_INJECT_TOKEN_THRESHOLD } from "./constants";
import { Tooltip } from "./Tooltip";

const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20MB/file (§6.5)
const MAX_FILES = 10; // 10 files/agent (§6.5)
const POLL_MS = 3000;
const ACCEPT =
  ".pdf,.docx,.txt,.md,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ALLOWED_EXTENSIONS = ["pdf", "docx", "txt", "md"];

function fileExtension(name: string): string {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

function formatTokens(count: number | null): string {
  if (count === null) return "—";
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

const STATUS_LABEL: Record<DocumentInfo["status"], string> = {
  pending: "waiting",
  processing: "processing",
  ready: "ready",
  failed: "failed",
};

export interface KnowledgePanelProps {
  /** null on /agents/new — uploads need a saved agent. */
  agentId: string | null;
  config: AgentConfig;
  patch: (partial: Partial<AgentConfig>) => void;
  /** Fires whenever the document list changes (editor derives ready tokens). */
  onDocumentsChange: (documents: DocumentInfo[]) => void;
}

export function KnowledgePanel({ agentId, config, patch, onDocumentsChange }: KnowledgePanelProps) {
  const [documents, setDocuments] = useState<DocumentInfo[] | null>(null);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initial load (edit mode only).
  useEffect(() => {
    if (!agentId) {
      setDocuments([]);
      return;
    }
    const controller = new AbortController();
    api.documents
      .list(agentId, controller.signal)
      .then(setDocuments)
      .catch(() => {
        if (!controller.signal.aborted) setDocuments([]);
      });
    return () => controller.abort();
  }, [agentId]);

  // Surface the list to the editor (engine warning + auto verdict).
  useEffect(() => {
    if (documents) onDocumentsChange(documents);
  }, [documents, onDocumentsChange]);

  // Poll every 3s while any file is pending/processing. Timeout-chained on
  // the documents state so each response schedules the next tick.
  useEffect(() => {
    if (!agentId || !documents) return;
    if (!documents.some((d) => d.status === "pending" || d.status === "processing")) return;
    const timer = window.setTimeout(() => {
      api.documents
        .list(agentId)
        .then(setDocuments)
        // Transient failure: touch state so the effect re-arms and retries.
        .catch(() => setDocuments((prev) => (prev ? [...prev] : prev)));
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [agentId, documents]);

  const refresh = useCallback(async () => {
    if (!agentId) return;
    try {
      setDocuments(await api.documents.list(agentId));
    } catch {
      // keep the current list; polling/next action will retry
    }
  }, [agentId]);

  async function handleFiles(fileList: FileList | File[]) {
    if (!agentId) return;
    setUploadError(null);
    const files = [...fileList];
    const problems: string[] = [];
    const accepted: File[] = [];

    const existing = documents?.length ?? 0;
    for (const file of files) {
      if (!ALLOWED_EXTENSIONS.includes(fileExtension(file.name))) {
        problems.push(`${file.name}: only PDF, DOCX, TXT, and MD files are supported.`);
      } else if (file.size > MAX_FILE_BYTES) {
        problems.push(`${file.name}: over the 20MB limit (${formatBytes(file.size)}).`);
      } else {
        accepted.push(file);
      }
    }
    if (existing + accepted.length > MAX_FILES) {
      const room = Math.max(0, MAX_FILES - existing);
      problems.push(
        `An agent can hold ${MAX_FILES} documents — ${room === 0 ? "delete one first" : `only ${room} more fit`}.`,
      );
      accepted.length = room;
    }

    if (accepted.length > 0) {
      setUploading((n) => n + accepted.length);
      const results = await Promise.allSettled(accepted.map((f) => api.documents.upload(agentId, f)));
      results.forEach((result, i) => {
        if (result.status === "rejected") {
          const reason = result.reason instanceof Error ? result.reason.message : "upload failed";
          problems.push(`${accepted[i].name}: ${reason}`);
        }
      });
      setUploading((n) => n - accepted.length);
      await refresh();
    }
    if (problems.length > 0) setUploadError(problems.join(" "));
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragActive(false);
    if (!agentId) return;
    if (event.dataTransfer.files.length > 0) void handleFiles(event.dataTransfer.files);
  }

  async function handleDelete(id: string) {
    setUploadError(null);
    try {
      await api.documents.remove(id);
      setDocuments((prev) => prev?.filter((d) => d.id !== id) ?? prev);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Could not delete the document.");
    }
  }

  async function handleReprocess(id: string) {
    setUploadError(null);
    try {
      await api.documents.reprocess(id);
      await refresh();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Could not reprocess the document.");
    }
  }

  const readyTokens = (documents ?? [])
    .filter((d) => d.status === "ready")
    .reduce((sum, d) => sum + (d.token_count ?? 0), 0);
  const readyCount = (documents ?? []).filter((d) => d.status === "ready").length;

  const dropzone = (
    <div
      role="button"
      tabIndex={agentId ? 0 : -1}
      aria-disabled={!agentId}
      onClick={() => agentId && fileInputRef.current?.click()}
      onKeyDown={(event) => {
        if (agentId && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          fileInputRef.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (agentId) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
      className={cx(
        "flex flex-col items-center justify-center gap-1.5 rounded border border-dashed px-4 py-8 text-center transition-colors",
        agentId ? "cursor-pointer" : "cursor-not-allowed opacity-60",
        dragActive ? "border-bright bg-surface-raised" : "border-line hover:border-muted",
      )}
    >
      <svg viewBox="0 0 20 20" className="h-5 w-5 text-muted" fill="none" aria-hidden="true">
        <path
          d="M10 13V4m0 0L6.5 7.5M10 4l3.5 3.5M4 13v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="text-sm text-body">
        {agentId ? "Drop files here, or click to browse" : "Save the agent first to add documents"}
      </span>
      <span className="text-xs text-muted">PDF, DOCX, TXT, MD — up to 20MB each, 10 per agent</span>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          if (event.target.files) void handleFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {dropzone}

      {uploadError && (
        <p className="rounded border border-danger/60 bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
          {uploadError}
        </p>
      )}

      {uploading > 0 && (
        <p className="text-xs text-muted">
          Uploading {uploading} file{uploading === 1 ? "" : "s"}…
        </p>
      )}

      {documents && documents.length > 0 && (
        <ul className="flex flex-col divide-y divide-line rounded border border-line">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm text-bright">{doc.filename}</span>
                <span className="font-mono text-xs text-muted">
                  {formatBytes(doc.size_bytes)}
                  {doc.status === "ready" && ` · ${formatTokens(doc.token_count)} tokens`}
                </span>
              </div>

              {doc.status === "failed" && doc.error ? (
                <Tooltip content={doc.error}>
                  <span className="inline-flex items-center gap-1.5 rounded border border-danger/60 px-1.5 py-0.5">
                    <StatusDot tone={documentStatusTone(doc.status)} />
                    <span className="text-xs text-danger">failed</span>
                  </span>
                </Tooltip>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded border border-line px-1.5 py-0.5">
                  <StatusDot
                    tone={documentStatusTone(doc.status)}
                    pulse={doc.status === "pending" || doc.status === "processing"}
                  />
                  <span className="text-xs text-body">{STATUS_LABEL[doc.status]}</span>
                </span>
              )}

              {doc.status === "failed" && (
                <Button size="sm" onClick={() => void handleReprocess(doc.id)}>
                  Retry
                </Button>
              )}
              <button
                type="button"
                onClick={() => void handleDelete(doc.id)}
                aria-label={`Delete ${doc.filename}`}
                className="shrink-0 rounded p-1.5 text-muted transition-colors hover:bg-surface-raised hover:text-bright"
              >
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      {readyCount > 0 && (
        <p className="text-xs text-muted">
          {readyCount} document{readyCount === 1 ? "" : "s"} ready ·{" "}
          <span className="font-mono">{formatTokens(readyTokens)}</span> tokens
          {config.knowledge_mode === "auto" && (
            <>
              {" — "}
              {readyTokens <= KNOWLEDGE_INJECT_TOKEN_THRESHOLD
                ? "small enough to give the agent whole."
                : "large, so the agent will search it per question."}
            </>
          )}
        </p>
      )}

      {/* Advanced: knowledge mode + passages per lookup */}
      <fieldset className="flex flex-col gap-2 border-t border-line pt-4">
        <legend className="sr-only">How the agent uses knowledge</legend>
        <span className="text-xs font-medium text-body">How the agent uses knowledge</span>
        <KnowledgeModeRadio
          value={config.knowledge_mode}
          onChange={(mode) => patch({ knowledge_mode: mode })}
        />
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium text-body">Passages per lookup</span>
          <span className="text-xs text-muted">Only used when searching</span>
        </div>
        <Slider
          value={config.retrieval_top_k}
          onChange={(v) => patch({ retrieval_top_k: v })}
          min={1}
          max={8}
          disabled={config.knowledge_mode === "inject"}
          label="Passages per lookup"
        />
      </div>
    </div>
  );
}

const MODE_OPTIONS: Array<{ value: KnowledgeMode; label: string; help: string }> = [
  {
    value: "auto",
    label: "Automatic",
    help: "Recommended. Small knowledge is given to the agent whole; large knowledge is searched per question.",
  },
  {
    value: "inject",
    label: "Always include everything",
    help: "Every document goes into the prompt on every call. Best kept for small knowledge bases.",
  },
  {
    value: "retrieve",
    label: "Search per question",
    help: "The agent looks up the most relevant passages for each question. Best for large knowledge bases.",
  },
];

function KnowledgeModeRadio({
  value,
  onChange,
}: {
  value: KnowledgeMode;
  onChange: (mode: KnowledgeMode) => void;
}) {
  return (
    <div role="radiogroup" aria-label="How the agent uses knowledge" className="flex flex-col gap-1.5">
      {MODE_OPTIONS.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cx(
              "flex items-start gap-2.5 rounded border px-3 py-2 text-left transition-colors",
              selected ? "border-bright bg-surface-raised" : "border-line hover:border-muted",
            )}
          >
            <span
              aria-hidden="true"
              className={cx(
                "mt-1 h-1.5 w-1.5 shrink-0 rounded-full",
                selected ? "bg-bright" : "bg-line",
              )}
            />
            <span className="flex flex-col gap-0.5">
              <span className={cx("text-sm", selected ? "text-bright" : "text-body")}>
                {option.label}
              </span>
              <span className="text-xs text-muted">{option.help}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
